import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { format, parseISO, isValid } from 'date-fns'
import { useState } from 'react'
import type React from 'react'

import type { AdverseCredit as AdverseCreditType } from '@/types/Activity'

interface Props {
  adverseCredit: AdverseCreditType[]
  hasConfirmedIdentity: boolean
  hasDeclaredCredit: boolean
  provider: string
}

const getStatusBoxColor = (status: string) => {
  const normalizedStatus = status.toLowerCase()
  if (normalizedStatus === 'unsatisfied')
    return { backgroundColor: '#FEE5E6', color: '#79050B' }
  if (normalizedStatus === 'satisfied')
    return { backgroundColor: '#FEEFDE', color: '#743D03' }
  return { backgroundColor: '#f5f5f5', color: '#616161' }
}

const formatCourtDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString)
    if (!isValid(date)) return dateString

    return format(date, 'do MMMM yyyy')
  } catch {
    return dateString
  }
}

const formatRowData = (data: AdverseCreditType) => ({
  ...data,
  address: data.address || 'Not found',
  caseNumber: data.caseNumber || 'Not found',
})

const Row: React.FC<{ row: ReturnType<typeof formatRowData> }> = ({ row }) => {
  const [open, setOpen] = useState(false)
  const toggleOpen = () => setOpen(prev => !prev)

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label={open ? 'collapse row' : 'expand row'}
            size="small"
            onClick={toggleOpen}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.type}
        </TableCell>
        <TableCell align="left">£{row.amount.toFixed(2)}</TableCell>
        <TableCell align="left">{row.address}</TableCell>
        <TableCell align="left">
          <Box
            sx={{
              ...getStatusBoxColor(row.status),
              borderRadius: '5px',
              padding: '4px 12px',
              display: 'inline-block',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {row.status}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                padding: 2,
                backgroundColor: '#F2F2F2',
                display: 'flex',
                justifyContent: 'space-evenly',
              }}
            >
              <Typography variant="body2">
                <strong>Case number:</strong> {row.caseNumber}
              </Typography>
              <Typography variant="body2">
                <strong>Court date:</strong> {formatCourtDate(row.courtDate)}
              </Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

const AdverseCredit = ({
  adverseCredit,
  hasConfirmedIdentity,
  hasDeclaredCredit,
  provider,
}: Props) => {
  const hasNoAdverseCredit = adverseCredit.length === 0

  return (
    <>
      <Typography
        variant="h6"
        component="h3"
        fontWeight={700}
        fontSize="14px"
        sx={{
          mt: 3,
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>Adverse credit</span>
        {!hasNoAdverseCredit && (
          <span style={{ color: '#4F4F4F' }}>({adverseCredit.length} found)</span>
        )}
      </Typography>
      <Typography fontWeight={400} fontSize="12px" sx={{ mb: 1 }}>
        Adverse credit items returned from the {provider} credit check
      </Typography>

      {hasNoAdverseCredit ? (
        !hasConfirmedIdentity ? (
          <Alert severity="warning" sx={{ borderRadius: 2, my: 2 }}>
            <AlertTitle>Unable to confirm applicant identity</AlertTitle>
            The applicant was not found on the electoral roll and has no open credit
            accounts. Review the {provider} report or request additional verification
            documents.
          </Alert>
        ) : hasDeclaredCredit ? (
          <Alert severity="warning" sx={{ borderRadius: 2, my: 2 }}>
            <AlertTitle>
              Declared adverse credit not found in {provider} report
            </AlertTitle>
            The applicant declared adverse credit, but it wasn't returned in the credit
            check. <b>Check the {provider} report manually</b> to verify using the
            addresses below.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 2, my: 2 }}>
            No adverse credit items found
          </Alert>
        )
      ) : (
        <TableContainer>
          <Table aria-label="Adverse Credit table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Type</TableCell>
                <TableCell align="left">Amount</TableCell>
                <TableCell align="left">Address</TableCell>
                <TableCell align="left">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adverseCredit.map((row, i) => (
                <Row
                  key={`${row.type}-${row.amount}-${
                    row.address?.trim() || 'no-address'
                  }-${row.status}-${i}`}
                  row={formatRowData(row)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}

export default AdverseCredit
